/**
 * src/App.jsx — routing and the authenticated shell.
 *
 * Two families of route:
 *  - **Protected** (/dashboard, /calendar, …) sit inside <RequireAuth> and the
 *    sidebar layout. They redirect to /login when there is no session.
 *  - **Public** (/book/:slug, /gallery/:slug, /sign/:token, /join/:token) have
 *    no chrome and no session at all — a client following a link from
 *    Instagram, or an artist opening their invite email, has no account yet.
 *
 * /settings additionally requires the owner role; an artist landing there is
 * sent back to the dashboard rather than shown a page of failing requests.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { AuthProvider, useAuth } from './lib/auth.jsx';
import { api } from './lib/api.js';
import { useApi } from './lib/useApi.js';
import { getMode } from './lib/theme.js';
import { ErrorBanner } from './components/ui.jsx';
import { ToastProvider } from './components/ui/Toast.jsx';
import { DashboardNav } from './components/ui/DashboardNav.jsx';
import ThemeSwitcher from './components/ThemeSwitcher.jsx';
import SubscriptionPaymentForm from './components/SubscriptionPaymentForm.jsx';

import Login from './pages/Login.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Calendar from './pages/Calendar.jsx';
import Clients from './pages/Clients.jsx';
import ClientProfile from './pages/ClientProfile.jsx';
import Projects from './pages/Projects.jsx';
import ProjectDetail from './pages/ProjectDetail.jsx';
import Flash from './pages/Flash.jsx';
import ConsentForms from './pages/ConsentForms.jsx';
import Settings from './pages/Settings.jsx';
import Account from './pages/Account.jsx';
import Inbox from './pages/Inbox.jsx';

import BookingPortal from './pages/public/BookingPortal.jsx';
import FlashGallery from './pages/public/FlashGallery.jsx';
import ConsentSign from './pages/public/ConsentSign.jsx';
import JoinInvite from './pages/public/JoinInvite.jsx';
import Unsubscribe from './pages/public/Unsubscribe.jsx';
import DesignSpecimen from './pages/DesignSpecimen.jsx';
import DesignGallery from './pages/DesignGallery.jsx';

// The domain each studio's own <slug>.<domain> subdomain sits under (TS-28/
// TS-39) — unset in dev, where the subdomain redirect below is inert. Mirrors
// lib/tenancy.js's own VITE_APP_BASE_DOMAIN read for the public pages.
const BASE_DOMAIN = import.meta.env.VITE_APP_BASE_DOMAIN;

/**
 * Gate for authenticated routes.
 *
 * Renders nothing while the session is still being resolved — redirecting
 * during that window would bounce a signed-in user out on every page refresh.
 *
 * Also sends a signed-in artist to their own studio's subdomain: the
 * dashboard is reached straight after /login or /auth/register on the shared
 * apex, same as any bookmark or stale link would land there, but a studio's
 * own pages should live at `<slug>.APP_BASE_DOMAIN` — same as their public
 * booking/gallery links already do (see lib/tenancy.js). A full navigation
 * (not client-side routing) is required since it is a different origin.
 *
 * @param {{ children: React.ReactNode, ownerOnly?: boolean }} props
 * @returns {JSX.Element}
 */
function RequireAuth({ children, ownerOnly = false }) {
  const { artist, loading, isOwner } = useAuth();
  const location = useLocation();

  const homeHost = artist?.studioSlug && BASE_DOMAIN ? `${artist.studioSlug}.${BASE_DOMAIN}` : null;
  const needsSubdomain = Boolean(homeHost) && window.location.hostname !== homeHost;

  useEffect(() => {
    if (needsSubdomain) {
      window.location.replace(`https://${homeHost}${location.pathname}${location.search}`);
    }
  }, [needsSubdomain, homeHost, location.pathname, location.search]);

  if (loading) return <div className="empty">Loading…</div>;
  // `state` carries where they were heading so Login can send them back.
  if (!artist) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (ownerOnly && !isOwner) return <Navigate to="/dashboard" replace />;
  if (needsSubdomain) return <div className="empty">Redirecting…</div>;
  return children;
}

/**
 * Paywall gate for authenticated routes, sitting inside RequireAuth (so it
 * only ever runs for a signed-in artist) and outside Shell (a locked-out
 * studio should not see the sidebar chrome).
 *
 * Fetches GET /api/studio directly rather than going through useAuth(),
 * since subscription status lives on the studio row, not the artist/session
 * payload — see routes/studios.js's publicStudio().
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
function RequireSubscription({ children }) {
  const { data, loading, error, reload } = useApi('/api/studio');
  const [params] = useSearchParams();
  const justReturned = params.get('subscribe') === 'return';

  // Returning from Stripe Checkout: the webhook that flips subscription_status
  // to 'active' may not have landed yet, so re-fetch rather than trust the
  // studio object this page mounted with.
  useEffect(() => {
    if (justReturned) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justReturned]);

  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <ErrorBanner>{error}</ErrorBanner>;

  const studio = data?.studio;
  if (studio && studio.subscriptionStatus !== 'active') {
    return <SubscribeGate studio={studio} justReturned={justReturned} reload={reload} />;
  }
  return children;
}

/**
 * Shown instead of the dashboard for a studio without an active
 * subscription — but the two ways a studio ends up here are treated
 * differently, per policy: paying is part of *setting up* a studio, not a
 * separate step reached after registering.
 *
 *  - `'incomplete'` (never finished setup) — the embedded payment form
 *    (SubscriptionPaymentForm) is fetched and shown automatically, no click
 *    required to reach it. This is the same screen an owner sees whether
 *    they just registered or are returning later having never completed
 *    payment; RequireSubscription is the one gate both paths go through.
 *  - `'past_due'` / `'canceled'` (was paying, stopped) — the only case that
 *    is actually a *block* requiring a deliberate click before the payment
 *    form appears. Resubscribing after a failed payment should never
 *    happen invisibly on page load.
 *
 * Only an owner can actually call `POST /api/studio/subscribe`
 * (owner-only) — an artist sees a message telling them to ask their owner,
 * not a control that would just 403.
 *
 * @param {{ studio: object, justReturned: boolean, reload: () => void }} props
 * @returns {JSX.Element}
 */
function SubscribeGate({ studio, justReturned, reload }) {
  const { isOwner } = useAuth();
  const [checkout, setCheckout] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Guards the auto-fetch effect below against firing twice (a re-render
  // before the request resolves, dev-mode double-invocation).
  const triggered = useRef(false);

  const startCheckout = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/studio/subscribe');
      setCheckout({ clientSecret: res.clientSecret, publishableKey: res.publishableKey });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const incomplete = studio.subscriptionStatus === 'incomplete';

  useEffect(() => {
    if (incomplete && isOwner && !triggered.current) {
      triggered.current = true;
      startCheckout();
    }
  }, [incomplete, isOwner, startCheckout]);

  const heading = incomplete ? 'Finishing your studio setup' : 'Your subscription needs attention';
  const waitingOnWebhook = confirmed || justReturned;

  // The payment itself is done at this point (either checkout.confirm()
  // resolved, or Stripe redirected back), but subscription_status only
  // flips once the billing webhook (routes/stripe.js) processes it — which
  // can lag by a few seconds. Poll GET /api/studio until it does; the
  // moment reload() picks up 'active', RequireSubscription stops rendering
  // this gate at all and unmounts it, which is what actually ends the
  // polling (not a fixed success condition checked here).
  useEffect(() => {
    if (!waitingOnWebhook) return undefined;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > 10) {
        clearInterval(interval);
        return;
      }
      reload();
    }, 2000);
    return () => clearInterval(interval);
  }, [waitingOnWebhook, reload]);

  return (
    <div className="public-page center">
      {/* .center only centres text; margin centres the narrower card itself
          inside the 720px page column. */}
      <div className="card stack" style={{ maxWidth: 420, margin: '0 auto' }}>
        <h1>{heading}</h1>
        {isOwner && !waitingOnWebhook ? (
          <p className="muted small">£49.99/month, cancel anytime.</p>
        ) : null}
        {!isOwner ? (
          <p className="muted small">
            Ask your studio owner to {incomplete ? `finish setting up ${studio.name}` : 'update billing'}.
          </p>
        ) : waitingOnWebhook ? (
          <>
            <p className="muted">
              We&rsquo;re confirming your payment with Stripe — this can take a moment. The
              dashboard will unlock automatically once it&rsquo;s through.
            </p>
            <button type="button" className="btn btn-sm" onClick={reload}>
              Check now
            </button>
          </>
        ) : checkout ? (
          <>
            {!incomplete ? (
              <p className="muted small">
                {studio.name}&rsquo;s last payment didn&rsquo;t go through — start a new
                subscription below to keep using the dashboard and taking bookings.
              </p>
            ) : null}
            <SubscriptionPaymentForm
              publishableKey={checkout.publishableKey}
              clientSecret={checkout.clientSecret}
              onSuccess={() => setConfirmed(true)}
            />
          </>
        ) : incomplete ? (
          <>
            <p className="muted">Setting up payment…</p>
            <ErrorBanner>{error}</ErrorBanner>
          </>
        ) : (
          <>
            <p className="muted">
              {studio.name}&rsquo;s last payment didn&rsquo;t go through. Update your billing to
              keep using the dashboard and taking bookings.
            </p>
            <ErrorBanner>{error}</ErrorBanner>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={startCheckout}>
              {busy ? 'Loading…' : 'Update billing'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** How often the sidebar re-checks the inbox's unread count. */
const UNREAD_POLL_MS = 60_000;

/**
 * The inbox's unread count for the sidebar badge.
 *
 * Polled while the tab is visible, re-checked when the tab regains focus
 * (useApi does that), and immediately when the Inbox page reports it just
 * read something (the `inbox:changed` event) — otherwise the badge would
 * lag a minute behind the thread the artist just opened. A failed fetch
 * just shows no badge; it is never worth an error on every page.
 *
 * @returns {number}
 */
function useInboxUnread() {
  const { data, reload } = useApi('/api/inbox/unread');

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') reload();
    };
    const interval = setInterval(tick, UNREAD_POLL_MS);
    window.addEventListener('inbox:changed', reload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('inbox:changed', reload);
    };
  }, [reload]);

  return data?.count ?? 0;
}

/**
 * The sidebar layout wrapping every protected page.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
function Shell({ children }) {
  const { artist, isOwner, logout } = useAuth();
  const [mode, setModeState] = useState(getMode);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const inboxUnread = useInboxUnread();

  // Belt-and-braces close: DashboardNav already closes the mobile overlay
  // when a nav link is clicked (see its `onNavigate` prop), but this also
  // catches navigation that doesn't go through a nav link — back/forward,
  // a redirect from a guard, etc.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Full-screen overlay: Escape closes it, and background content shouldn't
  // scroll behind it while it's open.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // The swatch already applied the mode and saved it to localStorage by the
  // time this fires (see ThemeSwitcher) — this also saves it to the
  // artist's account, so it follows them to another device. A failure here
  // is not worth surfacing: the mode still applied, it just stays local.
  //
  // 'system' has no backend equivalent — VALID_THEMES in
  // api/src/routes/artists.js only ever accepted concrete themes, and this
  // redesign doesn't touch that API (see docs/redesign/theme-migration.md)
  // — so a 'system' choice applies locally but isn't synced; only an
  // explicit light/dark choice is, exactly as the backend already expects.
  const handleModeChange = (next) => {
    setModeState(next);
    if (next !== 'system') {
      api.patch(`/api/artists/${artist.id}`, { theme: next }).catch(() => {});
    }
  };

  return (
    <div className="shell">
      {/* Mobile only (see the 760px breakpoint in styles.css) — the vertical
          sidebar below collapses into a horizontally-scrolling nav strip
          there, with no room left for its own footer, so sign-out needs a
          home that is always on screen rather than something to scroll to. */}
      <header className="mobile-topbar">
        <button
          type="button"
          className="menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="menu-toggle-bars" aria-hidden="true" />
        </button>
        <span className="mobile-topbar-brand">Tattoo Lab</span>
        <button type="button" className="btn btn-sm" onClick={logout}>
          Sign out
        </button>
      </header>

      <DashboardNav
        isOwner={isOwner}
        mobileOpen={menuOpen}
        onNavigate={() => setMenuOpen(false)}
        badges={{ '/inbox': inboxUnread }}
        footer={
          <div className="sidebar-footer stack">
            <div className="small">
              <strong>{artist?.name}</strong>
              <div className="muted">{artist?.role}</div>
            </div>
            <ThemeSwitcher value={mode} onChange={handleModeChange} />
            <button type="button" className="btn btn-sm" onClick={logout}>
              Sign out
            </button>
          </div>
        }
      />
      <main className="main">{children}</main>
    </div>
  );
}

/**
 * Wraps a page in the gate and the shell.
 *
 * @param {{ children: React.ReactNode, ownerOnly?: boolean }} props
 * @returns {JSX.Element}
 */
function Protected({ children, ownerOnly }) {
  return (
    <RequireAuth ownerOnly={ownerOnly}>
      <RequireSubscription>
        <Shell>{children}</Shell>
      </RequireSubscription>
    </RequireAuth>
  );
}

/**
 * Application root.
 *
 * @returns {JSX.Element}
 */
export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
          {/* Public client-facing pages — no chrome, no session.
              Each has two forms: the legacy `:slug`-in-path one, and — once a
              studio has its own <slug>.APP_BASE_DOMAIN subdomain (TS-28) — a
              slug-free one that resolves the studio from the hostname
              instead (see BookingPortal.jsx / FlashGallery.jsx). */}
          <Route path="/book/:slug" element={<BookingPortal />} />
          <Route path="/book/:slug/confirmed" element={<BookingPortal />} />
          <Route path="/book" element={<BookingPortal />} />
          <Route path="/book/confirmed" element={<BookingPortal />} />
          <Route path="/gallery/:slug" element={<FlashGallery />} />
          <Route path="/gallery" element={<FlashGallery />} />
          <Route path="/sign/:token" element={<ConsentSign />} />
          <Route path="/join/:token" element={<JoinInvite />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
          {/* REDESIGN.md Phase 1/3 — working design artefacts, not product
              pages. Reachable only by direct URL: no NAV entry, no link from
              anywhere production-facing, and marked noindex at runtime (see
              each page's own effect) since this SPA has one static
              index.html and no per-route server-rendered <head>. */}
          <Route path="/design-specimen" element={<DesignSpecimen />} />
          <Route path="/design-gallery" element={<DesignGallery />} />

          <Route path="/login" element={<Login />} />
          {/* Public account pages — no chrome, no session. The reset link's
              token is the only credential, like /sign/ and /join/. */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
          <Route path="/inbox/:threadId" element={<Protected><Inbox /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/clients/:id" element={<Protected><ClientProfile /></Protected>} />
          <Route path="/projects" element={<Protected><Projects /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/flash" element={<Protected><Flash /></Protected>} />
          <Route path="/consent" element={<Protected><ConsentForms /></Protected>} />
          <Route path="/settings" element={<Protected ownerOnly><Settings /></Protected>} />
          <Route path="/account" element={<Protected><Account /></Protected>} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="*"
            element={
              <div className="public-page center">
                <h1>Page not found</h1>
                <p className="muted">That link does not go anywhere.</p>
              </div>
            }
          />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}
